import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSite, useSiteLoading } from '@/services/siteData';
import { IcoDefrost, IcoShelfLife, IcoReheat } from '@/components/icons';
import { formatIngredients } from '@/utils/ingredients';
import { Seo, breadcrumbLd } from '@/components/Seo';
import { SITE_NAME, SITE_URL } from '@/config/site';

export function ProductDetailPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { getProductByCode, getRangeByKey, deriveFormat } = useSite();
  const loading = useSiteLoading();
  const product = getProductByCode(code);

  // While the live catalogue is still loading, don't bounce a direct deep-link
  // to home — wait for the load to settle (the product may only exist live).
  if (!product && loading) {
    return (
      <div className="page-fade">
        <div className="page-head">
          <p className="lede">Loading product…</p>
        </div>
      </div>
    );
  }

  if (!product) return <Navigate to="/" replace />;

  const range = getRangeByKey(product.rangeKey);
  const kind = range?.kind ?? 'order';
  const rangeLabel = range?.label ?? '';
  const weight = product.format ?? deriveFormat(product.name);
  const allergens = product.allergens?.join(', ');

  const path = `/product/${product.code}`;
  const seoDesc =
    product.description ||
    `${product.name} for wholesale and food service. Trade accounts only — supplied to restaurants, hotels and caterers across the UK.`;
  // Product photos are served at a stable, code-keyed URL. Make it absolute on
  // the canonical domain for og:image + JSON-LD (relative paths aren't valid there).
  const absImg = product.image
    ? /^https?:\/\//.test(product.image)
      ? product.image
      : `${SITE_URL}${product.image.startsWith('/') ? '' : '/'}${product.image}`
    : undefined;

  const productLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: product.code,
    ...(absImg ? { image: absImg } : {}),
    description: seoDesc,
    brand: { '@type': 'Brand', name: SITE_NAME },
    ...(rangeLabel ? { category: rangeLabel } : {}),
  };

  return (
    <div className="page-fade">
      <Seo
        title={`${product.name} — Wholesale Patisserie`}
        description={seoDesc}
        path={path}
        type="product"
        image={absImg}
        jsonLd={[
          productLd,
          breadcrumbLd([
            { name: 'Home', path: '/' },
            ...(range ? [{ name: rangeLabel, path: `/${kind}/${range.slug}` }] : []),
            { name: product.name, path },
          ]),
        ]}
      />
      <div className="pd-intro">
        <h1>{product.name}</h1>
      </div>

      <section className="pd-product">
        <div className="pd-details">
          <div className="pd-image">
            <img src={product.image} alt={product.name} />
          </div>

          <div className="pd-content">
            <div>
              {product.description && <p className="pd-description">{product.description}</p>}

              {product.code && (
                <p className="pd-line">
                  <span>Product code:</span> {product.code}
                </p>
              )}
              {weight && (
                <p className="pd-line">
                  <span>Weight:</span> {weight}
                </p>
              )}
              {product.packSize && (
                <p className="pd-line">
                  <span>Case size:</span> {product.packSize}
                </p>
              )}

              {(product.ingredients || allergens) && <hr />}

              {product.ingredients && (
                <p className="pd-ingredients">
                  <span>Ingredients:</span>{' '}
                  <span dangerouslySetInnerHTML={{ __html: formatIngredients(product.ingredients) }} />
                </p>
              )}
              {allergens && (
                <p className="pd-allergens">
                  <span>Allergens:</span> {allergens}
                </p>
              )}

              {(product.defrost || product.shelfLife || product.reheat) && (
                <div className="pd-icons">
                  {product.defrost && (
                    <p className="pd-icon" title="Defrost time">
                      <IcoDefrost /> {product.defrost}
                    </p>
                  )}
                  {product.reheat && (
                    <p className="pd-icon" title="Reheat">
                      <IcoReheat /> {product.reheat}
                    </p>
                  )}
                  {product.shelfLife && (
                    <p className="pd-icon" title="Shelf life">
                      <IcoShelfLife /> {product.shelfLife}
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              className="pd-back"
              onClick={() => navigate(range ? `/${kind}/${range.slug}` : '/')}
            >
              Back to all {rangeLabel || (kind === 'order' ? 'Available to Order' : 'Portfolio')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
